import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AI Avatar Component - ASTRA's Conversational AI Interface
 *
 * PRODUCTION VERSION (2025-12-29): Animated AI Avatar for voice interactions
 *
 * Features:
 * - Animated 3D-like avatar using CSS/SVG
 * - Lip-sync animation when speaking
 * - Reactive pulse effects when listening
 * - Emotional state indicators
 * - Particle effects for engagement
 * - Holographic design aesthetic
 *
 * Inspired by: HeyGen, D-ID, and Synthesia conversational avatars
 */
const AIAvatar = ({
  isListening = false,
  isSpeaking = false,
  isThinking = false,
  voiceLevel = 0,
  mood = 'neutral', // 'neutral', 'happy', 'thinking', 'alert'
  size = 'large', // 'small', 'medium', 'large'
  onAvatarClick,
  className = ''
}) => {
  const [particles, setParticles] = useState([]);
  const [eyeBlink, setEyeBlink] = useState(false);
  const canvasRef = useRef(null);

  // Size configurations
  const sizeConfig = {
    small: { container: 120, avatar: 80, ring: 100 },
    medium: { container: 200, avatar: 140, ring: 170 },
    large: { container: 300, avatar: 220, ring: 260 }
  };

  const config = sizeConfig[size];

  // Eye blink animation
  useEffect(() => {
    const blinkInterval = setInterval(() => {
      setEyeBlink(true);
      setTimeout(() => setEyeBlink(false), 150);
    }, 3000 + Math.random() * 2000);

    return () => clearInterval(blinkInterval);
  }, []);

  // Generate particles when active
  useEffect(() => {
    if (isListening || isSpeaking) {
      const interval = setInterval(() => {
        const newParticle = {
          id: Date.now() + Math.random(),
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: 2 + Math.random() * 4,
          duration: 1 + Math.random() * 2
        };
        setParticles(prev => [...prev.slice(-15), newParticle]);
      }, 200);

      return () => clearInterval(interval);
    }
  }, [isListening, isSpeaking]);

  // Mood colors
  const moodColors = {
    neutral: { primary: '#3B82F6', secondary: '#60A5FA', glow: 'rgba(59, 130, 246, 0.5)' },
    happy: { primary: '#10B981', secondary: '#34D399', glow: 'rgba(16, 185, 129, 0.5)' },
    thinking: { primary: '#8B5CF6', secondary: '#A78BFA', glow: 'rgba(139, 92, 246, 0.5)' },
    alert: { primary: '#F59E0B', secondary: '#FBBF24', glow: 'rgba(245, 158, 11, 0.5)' }
  };

  const colors = moodColors[mood];

  // Lip movement based on speaking
  const lipHeight = isSpeaking ? 8 + Math.sin(Date.now() / 50) * 4 : 4;

  return (
    <div
      className={`relative flex items-center justify-center ${className}`}
      style={{ width: config.container, height: config.container }}
      onClick={onAvatarClick}
    >
      {/* Outer Glow Ring */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: config.ring,
          height: config.ring,
          background: `radial-gradient(circle, ${colors.glow} 0%, transparent 70%)`,
        }}
        animate={{
          scale: isListening ? [1, 1.1, 1] : isSpeaking ? [1, 1.05, 1] : 1,
          opacity: isListening || isSpeaking ? [0.5, 0.8, 0.5] : 0.3
        }}
        transition={{
          duration: isListening ? 0.5 : 1,
          repeat: Infinity,
          ease: 'easeInOut'
        }}
      />

      {/* Pulsing Rings */}
      {(isListening || isSpeaking) && (
        <>
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute rounded-full border-2"
              style={{
                width: config.ring - 20,
                height: config.ring - 20,
                borderColor: colors.primary
              }}
              initial={{ scale: 0.8, opacity: 0.8 }}
              animate={{ scale: 1.3, opacity: 0 }}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: i * 0.5,
                ease: 'easeOut'
              }}
            />
          ))}
        </>
      )}

      {/* Voice Level Indicator Ring */}
      {isListening && (
        <svg
          className="absolute"
          width={config.ring}
          height={config.ring}
          style={{ transform: 'rotate(-90deg)' }}
        >
          <circle
            cx={config.ring / 2}
            cy={config.ring / 2}
            r={(config.ring / 2) - 5}
            fill="none"
            stroke={colors.primary}
            strokeWidth="3"
            strokeDasharray={`${(voiceLevel / 100) * Math.PI * (config.ring - 10)} ${Math.PI * (config.ring - 10)}`}
            style={{ transition: 'stroke-dasharray 0.1s ease' }}
          />
        </svg>
      )}

      {/* Main Avatar Container */}
      <motion.div
        className="relative rounded-full overflow-hidden"
        style={{
          width: config.avatar,
          height: config.avatar,
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          boxShadow: `0 0 40px ${colors.glow}, inset 0 0 30px rgba(255,255,255,0.1)`
        }}
        animate={{
          scale: isThinking ? [1, 0.98, 1] : 1,
          rotate: isThinking ? [0, 1, -1, 0] : 0
        }}
        transition={{
          duration: 2,
          repeat: isThinking ? Infinity : 0,
          ease: 'easeInOut'
        }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Inner Face */}
        <div className="absolute inset-4 rounded-full bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center">
          {/* Face Features */}
          <svg
            viewBox="0 0 100 100"
            className="w-full h-full"
          >
            {/* Face Glow */}
            <defs>
              <radialGradient id="faceGlow" cx="50%" cy="30%" r="60%">
                <stop offset="0%" stopColor={colors.primary} stopOpacity="0.3" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Background Glow */}
            <circle cx="50" cy="50" r="45" fill="url(#faceGlow)" />

            {/* Left Eye */}
            <motion.ellipse
              cx="35"
              cy="40"
              rx="8"
              ry={eyeBlink ? 1 : 6}
              fill={colors.primary}
              filter="url(#glow)"
              animate={{
                ry: eyeBlink ? 1 : isListening ? [5, 7, 5] : 6
              }}
              transition={{ duration: 0.5 }}
            />
            {/* Left Eye Shine */}
            <circle cx="37" cy="38" r="2" fill="white" opacity="0.8" />

            {/* Right Eye */}
            <motion.ellipse
              cx="65"
              cy="40"
              rx="8"
              ry={eyeBlink ? 1 : 6}
              fill={colors.primary}
              filter="url(#glow)"
              animate={{
                ry: eyeBlink ? 1 : isListening ? [5, 7, 5] : 6
              }}
              transition={{ duration: 0.5 }}
            />
            {/* Right Eye Shine */}
            <circle cx="67" cy="38" r="2" fill="white" opacity="0.8" />

            {/* Mouth / Speaking Animation */}
            <motion.ellipse
              cx="50"
              cy="65"
              rx="12"
              ry={isSpeaking ? lipHeight : 4}
              fill={colors.secondary}
              filter="url(#glow)"
              animate={{
                ry: isSpeaking ? [4, 8, 4, 10, 4] : mood === 'happy' ? 6 : 4
              }}
              transition={{
                duration: isSpeaking ? 0.3 : 0.5,
                repeat: isSpeaking ? Infinity : 0
              }}
            />

            {/* Smile Curve (when happy) */}
            {mood === 'happy' && !isSpeaking && (
              <path
                d="M 38 60 Q 50 75 62 60"
                stroke={colors.secondary}
                strokeWidth="3"
                fill="none"
                filter="url(#glow)"
              />
            )}

            {/* Thinking Indicator */}
            {isThinking && (
              <>
                {[0, 1, 2].map((i) => (
                  <motion.circle
                    key={i}
                    cx={75 + i * 8}
                    cy={25}
                    r="3"
                    fill={colors.primary}
                    initial={{ opacity: 0.3 }}
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{
                      duration: 1,
                      repeat: Infinity,
                      delay: i * 0.2
                    }}
                  />
                ))}
              </>
            )}
          </svg>
        </div>

        {/* Holographic Scanline Effect */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(transparent 0%, rgba(255,255,255,0.03) 50%, transparent 100%)',
            backgroundSize: '100% 4px'
          }}
          animate={{ y: [0, config.avatar] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        />
      </motion.div>

      {/* Floating Particles */}
      <AnimatePresence>
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: particle.size,
              height: particle.size,
              backgroundColor: colors.primary,
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              boxShadow: `0 0 ${particle.size * 2}px ${colors.glow}`
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: [0, 1, 0], scale: [0, 1, 0], y: -50 }}
            exit={{ opacity: 0 }}
            transition={{ duration: particle.duration }}
          />
        ))}
      </AnimatePresence>

      {/* Status Label */}
      <motion.div
        className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-slate-900/80 border border-slate-700">
          <motion.div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: colors.primary }}
            animate={{
              scale: isListening || isSpeaking ? [1, 1.2, 1] : 1,
              opacity: isListening || isSpeaking ? [0.5, 1, 0.5] : 0.7
            }}
            transition={{ duration: 0.5, repeat: Infinity }}
          />
          <span className="text-xs font-medium text-slate-300">
            {isThinking ? 'Thinking...' : isSpeaking ? 'Speaking...' : isListening ? 'Listening...' : 'ASTRA AI'}
          </span>
        </div>
      </motion.div>
    </div>
  );
};

export default AIAvatar;
